import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { reportService } from '../services/apiService.jsx';
import FYWeekPicker from '../components/FYWeekPicker'; // [EDITED: KPI Task - Mahesh Sharma]
import './Reports.css';

const Reports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);

  const [filters, setFilters] = useState({
    select_year: '',
    select_month: '',
    select_week: '',
    created_date: '',
    start_date: '',
    end_date: '',
  });

  const [search, setSearch] = useState({ project: '', employee: '' });

  useEffect(() => {
    populateYearDropdown();
    // default date to today
    setFilters((f) => ({ ...f, created_date: new Date().toISOString().split('T')[0] }));
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  function populateYearDropdown() {
    const currentYear = new Date().getFullYear();
    const opts = [];
    for (let y = currentYear; y >= 2022; y--) opts.push(y);
    setYearOptions(opts);
  }

  async function fetchData() {
    setLoading(true);
    try {
      const response = await reportService.getReports(filters);
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setRows(data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  function parseMinutesFromString(hoursDiff) {
    if (!hoursDiff) return 0;
    const hoursMatch = hoursDiff.match(/(\d+)\s*hours?/i);
    const minutesMatch = hoursDiff.match(/(\d+)\s*minutes?/i);
    const h = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const m = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    return h * 60 + m;
  }

  // Direct click handler for date inputs
  const handleDateInputClick = (e) => {
    e.stopPropagation();
    const input = e.currentTarget
      .closest(".input-group, .date-picker-wrapper, .month-picker-wrapper, .week-picker-wrapper, .year-picker-wrapper")
      ?.querySelector('input[type="date"], input[type="month"], input[type="week"], select');
    if (input) {
      if (input.showPicker) {
        input.showPicker();
      } else {
        input.focus();
      }
    }
  };

  // Pagination state
  const [page, setPage] = useState(1);
  const rowsPerPage = 50;

  // Filtered rows
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const proj = (r.project_name || '').toLowerCase();
      const emp = (r.employee_name || '').toLowerCase();
      const matchProject = search.project === '' || proj.includes(search.project.toLowerCase());
      const matchEmployee = search.employee === '' || emp.includes(search.employee.toLowerCase());
      return matchProject && matchEmployee;
    });
  }, [rows, search]);

  // Paginated rows
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  const visibleRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page]);

  const totalMinutes = useMemo(() => {
    return filteredRows.reduce((acc, r) => acc + parseMinutesFromString(r.hours_diff || '0 hours 0 minutes'), 0);
  }, [filteredRows]);

  function minutesToHoursMinutes(total) {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${h} hours ${m} minutes`;
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(1); // Reset to first page on filter change
  }

  function handleSearchChange(e) {
    const { name, value } = e.target;
    setSearch((s) => ({ ...s, [name]: value }));
    setPage(1); // Reset to first page on search
  }

  function downloadCSV() {
    const headers = [
      'Employee Name',
      'Client',
      'Project',
      'Category',
      'Description',
      'Start Time',
      'End Time',
      'Worked Hours',
      'Status',
    ];
    const rowsData = visibleRows.map((r) => [
      r.employee_name || '',
      r.customer_name || '',
      r.project_name || '',
      r.catogery_name || '',
      r.description || '',
      r.created_date || '',
      r.updated_date || '',
      r.hours_diff || '',
      r.status || '',
    ]);

    const csvContent = [headers, ...rowsData].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const selectedDate = filters.created_date || new Date().toISOString().split('T')[0];
    a.download = `report_data_${selectedDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Layout>
      <main className="main">
        <div className="pagetitle">
          <div className="row d-flex align-items-center">
            <h1 style={{ color: '#76df23' }}>Reports</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/index">Home</a></li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </nav>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-9"></div>
                  <div className="col-md-3 text-end">
                    <button
                      className="btn btn-success d-inline-flex align-items-center justify-content-center"
                      onClick={downloadCSV}
                      id="downloadExcel"
                      title="Download Excel"
                      aria-label="Download Excel"
                      style={{ width: '38px', padding: 0 }}
                    >
                      <i className="bi bi-file-earmark-excel"></i>
                    </button>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-2">
                    <label>Select Year</label>
                    <div className="year-picker-wrapper" style={{ position: 'relative' }}>
                      <select 
                        name="select_year" 
                        className="form-control" 
                        value={filters.select_year} 
                        onChange={handleFilterChange}
                        onClick={handleDateInputClick}
                        style={{ paddingRight: '30px', appearance: 'none' }}
                      >
                        <option value="">Select Year</option>
                        {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                      <i 
                        className="bi bi-calendar3" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6c757d'
                        }}
                      ></i>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label>Select Month</label>
                    <div className="month-picker-wrapper" style={{ position: 'relative' }}>
                      <input 
                        type="month" 
                        name="select_month" 
                        className="form-control hide-default-picker" 
                        value={filters.select_month} 
                        onChange={handleFilterChange}
                        onClick={handleDateInputClick}
                        style={{ paddingRight: '30px' }}
                      />
                      <i 
                        className="bi bi-calendar3" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6c757d',
                          zIndex: 1
                        }}
                      ></i>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label d-block">Select Week</label>
                    <FYWeekPicker // [EDITED: KPI Task - Mahesh Sharma]
                      value={filters.select_week} // [EDITED: KPI Task - Mahesh Sharma]
                      onChange={(val) => setFilters(f => ({ ...f, select_week: val }))} // [EDITED: KPI Task - Mahesh Sharma]
                      size="sm" // [EDITED: KPI Task - Mahesh Sharma]
                      style={{ width: '100%', minWidth: 'unset' }} // [EDITED: KPI Task - Mahesh Sharma]
                    /> {/* [EDITED: KPI Task - Mahesh Sharma] */}
                  </div>
                  <div className="col-md-2">
                    <label>Select Date</label>
                    <div className="date-picker-wrapper" style={{ position: 'relative' }}>
                      <input 
                        type="date" 
                        name="created_date" 
                        className="form-control hide-default-picker" 
                        value={filters.created_date} 
                        onChange={handleFilterChange}
                        onClick={handleDateInputClick}
                        style={{ paddingRight: '30px' }}
                      />
                      <i 
                        className="bi bi-calendar3" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6c757d',
                          zIndex: 1
                        }}
                      ></i>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Custom Filter</label>
                    <div className="date-picker-wrapper" style={{ position: 'relative', marginBottom: '8px' }}>
                      <input 
                        type="date" 
                        name="start_date" 
                        className="form-control hide-default-picker" 
                        value={filters.start_date} 
                        onChange={handleFilterChange} 
                        placeholder="Start Date"
                        onClick={handleDateInputClick}
                        style={{ paddingRight: '30px' }}
                      />
                      <i 
                        className="bi bi-calendar3" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6c757d',
                          zIndex: 1
                        }}
                      ></i>
                    </div>
                    <div className="date-picker-wrapper" style={{ position: 'relative' }}>
                      <input 
                        type="date" 
                        name="end_date" 
                        className="form-control hide-default-picker" 
                        value={filters.end_date} 
                        onChange={handleFilterChange} 
                        placeholder="End Date"
                        onClick={handleDateInputClick}
                        style={{ paddingRight: '30px' }}
                      />
                      <i 
                        className="bi bi-calendar3" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6c757d',
                          zIndex: 1
                        }}
                      ></i>
                    </div>
                  </div>
                  <div className="col-md-3 mt-2">
                    <label className="form-label">Search Employee</label>
                    <input type="text" name="employee" id="employeeSearch" className="form-control" placeholder="Enter Employee name" value={search.employee} onChange={handleSearchChange} />
                  </div>
                  <div className="col-md-3 mt-2">
                    <label className="form-label">Search Project</label>
                    <input type="text" name="project" id="projectSearch" className="form-control" placeholder="Enter project name" value={search.project} onChange={handleSearchChange} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive-scroll" style={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}>
                  <table className="table table-bordered compact-table">
                    <thead>
                      <tr>
                        <th>Employee Name</th>
                        <th>Client</th>
                        <th>Project</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Worked Hours</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody id="reports2">
                      {loading ? (
                        <tr><td colSpan={9} className="text-center">Loading...</td></tr>
                      ) : visibleRows.length === 0 ? (
                        <tr><td colSpan={9} className="text-center">No records found</td></tr>
                      ) : (
                        visibleRows.map((r, idx) => (
                          <tr key={r.id || idx}>
                            <td>{r.employee_name}</td>
                            <td>{r.customer_name}</td>
                            <td>{r.project_name}</td>
                            <td>{r.catogery_name}</td>
                            <td>{r.description}</td>
                            <td>{r.created_date}</td>
                            <td>{r.updated_date}</td>
                            <td>{r.hours_diff || '0 hours 0 minutes'}</td>
                            <td>{r.status}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'right' }}>Total Worked Hours:</td>
                        <td id="totalWorkedHours">{minutesToHoursMinutes(totalMinutes)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Pagination Controls */}
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 16, gap: 8}}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{minWidth: 36}}
                  >
                    &lt;
                  </button>
                  <span style={{fontSize: '1rem', margin: '0 8px'}}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{minWidth: 36}}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Reports;